import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useMediaQuery } from '../lib/useMediaQuery';

type SortKey = 'alpha' | 'change' | 'vol' | 'rsi' | 'oi';
type SortDir = 'asc' | 'desc';
type FilterKey = 'pct' | 'vol' | 'rsi' | 'oi';

type Ticker = {
  symbol: string;
  lastPrice: number;
  priceChangePercent: number;
  volume?: number;
  quoteVolume: number;
};

type MarketKind = 'futures' | 'spot';

type Props = {
  value: string;
  options: string[];
  onPick: (symbol: string) => void;
  filter: string;
  onFilter: (v: string) => void;
  /** Per-tab market — default futures. */
  marketType?: MarketKind;
  onMarketType?: (m: MarketKind) => void;
};

const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
const fmtCompact = (n: number) => {
  if (!(n > 0)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  if (n >= 1) return n.toFixed(0);
  // Thin spot quoters — still show a readable crumb instead of “—”.
  if (n >= 0.01) return n.toFixed(2);
  return n.toFixed(4);
};

/** Parse filter number; vol/oi accept K/M/B suffix (e.g. 5M → 5e6). */
const parseBound = (raw: string, compact = false): number | null => {
  const s = raw.trim().replace(',', '.').toUpperCase();
  if (!s) return null;
  const m = /^([+-]?\d*\.?\d+)\s*([KMB])?$/.exec(s);
  if (!m) return null;
  let n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  if (compact) {
    const u = m[2];
    if (u === 'K') n *= 1e3;
    else if (u === 'M') n *= 1e6;
    else if (u === 'B') n *= 1e9;
  }
  return n;
};

export default function PairPicker({
  value, options, onPick, filter, onFilter,
  marketType = 'futures', onMarketType,
}: Props) {
  const { t } = useI18n();
  const isSpot = marketType === 'spot';
  const [open, setOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('change');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [usePct, setUsePct] = useState(false);
  const [useVol, setUseVol] = useState(false);
  const [useRsi, setUseRsi] = useState(false);
  const [useOi, setUseOi] = useState(false);
  const [pctMin, setPctMin] = useState('');
  const [pctMax, setPctMax] = useState('');
  const [volMin, setVolMin] = useState('');
  const [volMax, setVolMax] = useState('');
  const [rsiMin, setRsiMin] = useState('');
  const [rsiMax, setRsiMax] = useState('');
  const [oiMin, setOiMin] = useState('');
  const [oiMax, setOiMax] = useState('');
  const [tickers, setTickers] = useState<Record<string, Ticker>>({});
  const [rsiMap, setRsiMap] = useState<Record<string, number | null>>({});
  const [oiMap, setOiMap] = useState<Record<string, number | null>>({});
  const rsiMapRef = useRef(rsiMap);
  const oiMapRef = useRef(oiMap);
  rsiMapRef.current = rsiMap;
  oiMapRef.current = oiMap;
  const [busy, setBusy] = useState(false);
  const [rsiBusy, setRsiBusy] = useState(false);
  const [oiBusy, setOiBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Phone panel is ~96vw — fr tracks must shrink (minmax 0) or 5 Futures cols wrap/stack.
  const narrow = useMediaQuery('(max-width: 900px)');

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event) => {
      const node = e.target as Node | null;
      if (node && rootRef.current?.contains(node)) return;
      setOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  // Always a concrete string (never undefined) so Spot↔Futures never clears columns.
  // minmax(0, …fr) lets metric cols shrink on a phone instead of exploding the row.
  const gridCols = isSpot
    ? 'minmax(0, 1.5fr) minmax(0, 0.85fr) minmax(0, 0.75fr) minmax(0, 0.55fr)'
    : 'minmax(0, 1.35fr) minmax(0, 0.7fr) minmax(0, 0.55fr) minmax(0, 0.45fr) minmax(0, 0.6fr)';

  // Spot has no OI — drop OI sort/filter when switching.
  useEffect(() => {
    if (!isSpot) return;
    if (sortKey === 'oi') setSortKey('change');
    if (useOi) setUseOi(false);
  }, [isSpot, sortKey, useOi]);

  // Market flip: wipe boards immediately so Futures↔Spot don't mix “—” / wrong RSI/OI,
  // and never leave gridTemplateColumns: undefined (React clears the prior 4-col → table explodes).
  useEffect(() => {
    setTickers({});
    setRsiMap({});
    setOiMap({});
    setBusy(false);
    setRsiBusy(false);
    setOiBusy(false);
  }, [marketType]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBusy(true);
    void (async () => {
      try {
        const q = marketType === 'spot' ? '?market=spot' : '';
        const data = await api<{ tickers: Ticker[] }>(`/api/market/tickers${q}`);
        if (cancelled) return;
        const map: Record<string, Ticker> = {};
        for (const row of data.tickers || []) map[row.symbol] = row;
        setTickers(map);
      } catch {
        /* keep last */
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, marketType]);

  const mergeBoard = (
    prev: Record<string, number | null>,
    next: Record<string, number | null> | undefined,
  ) => {
    if (!next) return prev;
    const out = { ...prev };
    for (const [sym, v] of Object.entries(next)) {
      // Never overwrite a good value with null (partial / rate-limit blips).
      if (v != null && Number.isFinite(v)) out[sym] = v;
      else if (!(sym in out)) out[sym] = null;
    }
    return out;
  };

  /**
   * Board queue = what the user actually sees (sorted rows), movers/vol first as a
   * warm seed before tickers arrive. Progressive fill keeps retrying visible “—” cells.
   */
  const boardSeed = useMemo(() => {
    const byChg = [...options]
      .map((s) => ({ s, v: Math.abs(Number(tickers[s]?.priceChangePercent) || 0) }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 100)
      .map((x) => x.s);
    const byVol = [...options]
      .map((s) => ({ s, v: tickers[s]?.quoteVolume || 0 }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 80)
      .map((x) => x.s);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of [...byChg, ...byVol, value].filter(Boolean) as string[]) {
      if (seen.has(s)) continue;
      seen.add(s);
      out.push(s);
      if (out.length >= 160) break;
    }
    return out;
  }, [options, tickers, value]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'alpha' ? 'asc' : 'desc');
    }
  };

  const sortMark = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortDir === 'desc' ? ' ↓' : ' ↑';
  };

  const rows = useMemo(() => {
    const q = filter.trim().toUpperCase();
    const pLo = usePct ? parseBound(pctMin) : null;
    const pHi = usePct ? parseBound(pctMax) : null;
    const vLo = useVol ? parseBound(volMin, true) : null;
    const vHi = useVol ? parseBound(volMax, true) : null;
    const rLo = useRsi ? parseBound(rsiMin) : null;
    const rHi = useRsi ? parseBound(rsiMax) : null;
    const oLo = useOi ? parseBound(oiMin, true) : null;
    const oHi = useOi ? parseBound(oiMax, true) : null;

    const ch = (s: string) => tickers[s]?.priceChangePercent;
    const vol = (s: string) => {
      const tk = tickers[s];
      if (!tk) return undefined;
      if (tk.quoteVolume > 0) return tk.quoteVolume;
      const base = Number(tk.volume) || 0;
      const px = Number(tk.lastPrice) || 0;
      return base > 0 && px > 0 ? base * px : tk.quoteVolume;
    };
    const rsi = (s: string) => {
      const v = rsiMap[s];
      return v == null || !Number.isFinite(v) ? null : v;
    };
    const oi = (s: string) => {
      const v = oiMap[s];
      return v == null || !Number.isFinite(v) ? null : v;
    };

    let list = options.filter((s) => {
      if (q && !s.includes(q)) return false;
      if (usePct && (pLo != null || pHi != null)) {
        const pct = ch(s);
        if (pct == null || !Number.isFinite(pct)) return false;
        if (pLo != null && pct < pLo) return false;
        if (pHi != null && pct > pHi) return false;
      }
      if (useVol && (vLo != null || vHi != null)) {
        const v = vol(s);
        if (v == null || !Number.isFinite(v)) return false;
        if (vLo != null && v < vLo) return false;
        if (vHi != null && v > vHi) return false;
      }
      if (useRsi && (rLo != null || rHi != null)) {
        const r = rsi(s);
        if (r == null) return false;
        if (rLo != null && r < rLo) return false;
        if (rHi != null && r > rHi) return false;
      }
      if (useOi && (oLo != null || oHi != null)) {
        const o = oi(s);
        if (o == null) return false;
        if (oLo != null && o < oLo) return false;
        if (oHi != null && o > oHi) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'alpha') return dir * a.localeCompare(b);
      if (sortKey === 'change') {
        const ca = ch(a) ?? (sortDir === 'asc' ? Infinity : -Infinity);
        const cb = ch(b) ?? (sortDir === 'asc' ? Infinity : -Infinity);
        return dir * (ca - cb);
      }
      if (sortKey === 'vol') {
        return dir * ((vol(a) ?? -1) - (vol(b) ?? -1));
      }
      if (sortKey === 'oi') {
        const oa = oi(a);
        const ob = oi(b);
        if (oa == null && ob == null) return a.localeCompare(b);
        if (oa == null) return 1;
        if (ob == null) return -1;
        return dir * (oa - ob);
      }
      const ra = rsi(a);
      const rb = rsi(b);
      if (ra == null && rb == null) return a.localeCompare(b);
      if (ra == null) return 1;
      if (rb == null) return -1;
      return dir * (ra - rb);
    });
    return list.slice(0, 120);
  }, [
    options, filter, sortKey, sortDir, tickers, rsiMap, oiMap,
    usePct, useVol, useRsi, useOi,
    pctMin, pctMax, volMin, volMax, rsiMin, rsiMax, oiMin, oiMax,
  ]);

  /**
   * Fill queue mirrors visible list but does NOT depend on rsiMap/oiMap —
   * otherwise every partial merge would cancel in-flight chunks and leave “—”.
   */
  const fillTargets = useMemo(() => {
    const q = filter.trim().toUpperCase();
    const pLo = usePct ? parseBound(pctMin) : null;
    const pHi = usePct ? parseBound(pctMax) : null;
    const vLo = useVol ? parseBound(volMin, true) : null;
    const vHi = useVol ? parseBound(volMax, true) : null;
    const ch = (s: string) => tickers[s]?.priceChangePercent;
    const vol = (s: string) => {
      const tk = tickers[s];
      if (!tk) return undefined;
      if (tk.quoteVolume > 0) return tk.quoteVolume;
      const base = Number(tk.volume) || 0;
      const px = Number(tk.lastPrice) || 0;
      return base > 0 && px > 0 ? base * px : tk.quoteVolume;
    };
    let list = options.filter((s) => {
      if (q && !s.includes(q)) return false;
      if (usePct && (pLo != null || pHi != null)) {
        const pct = ch(s);
        if (pct == null || !Number.isFinite(pct)) return false;
        if (pLo != null && pct < pLo) return false;
        if (pHi != null && pct > pHi) return false;
      }
      if (useVol && (vLo != null || vHi != null)) {
        const v = vol(s);
        if (v == null || !Number.isFinite(v)) return false;
        if (vLo != null && v < vLo) return false;
        if (vHi != null && v > vHi) return false;
      }
      return true;
    });
    const sortAs = sortKey === 'rsi' || sortKey === 'oi' ? 'change' : sortKey;
    list = [...list].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortAs === 'alpha') return dir * a.localeCompare(b);
      if (sortAs === 'vol') return dir * ((vol(a) ?? -1) - (vol(b) ?? -1));
      const ca = ch(a) ?? (sortDir === 'asc' ? Infinity : -Infinity);
      const cb = ch(b) ?? (sortDir === 'asc' ? Infinity : -Infinity);
      return dir * (ca - cb);
    });
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of [...list.slice(0, 120), ...boardSeed]) {
      if (seen.has(s)) continue;
      seen.add(s);
      out.push(s);
      if (out.length >= 160) break;
    }
    return out;
  }, [
    options, filter, sortKey, sortDir, tickers, boardSeed,
    usePct, useVol, pctMin, pctMax, volMin, volMax,
  ]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => { window.setTimeout(r, ms); });
    setRsiBusy(true);
    void (async () => {
      try {
        for (let pass = 0; pass < 5; pass++) {
          if (cancelled) return;
          const missing = fillTargets.filter((s) => {
            const v = rsiMapRef.current[s];
            return v == null || !Number.isFinite(v);
          });
          if (!missing.length) break;
          for (let i = 0; i < missing.length; i += 25) {
            if (cancelled) return;
            const chunk = missing.slice(i, i + 25);
            try {
              const m = marketType === 'spot' ? '&market=spot' : '';
              const q = `?symbols=${encodeURIComponent(chunk.join(','))}&timeframe=1h&limit=${chunk.length}${m}`;
              const data = await api<{ rsi: Record<string, number | null> }>(`/api/market/rsi-board${q}`);
              if (cancelled) return;
              setRsiMap((prev) => mergeBoard(prev, data.rsi));
            } catch { /* keep trying */ }
            await sleep(160);
          }
          await sleep(260);
        }
      } finally {
        if (!cancelled) setRsiBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, fillTargets, marketType]);

  useEffect(() => {
    if (!open || isSpot) {
      setOiBusy(false);
      return;
    }
    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => { window.setTimeout(r, ms); });
    setOiBusy(true);
    void (async () => {
      try {
        for (let pass = 0; pass < 5; pass++) {
          if (cancelled) return;
          const missing = fillTargets.filter((s) => {
            const v = oiMapRef.current[s];
            return v == null || !Number.isFinite(v);
          });
          if (!missing.length) break;
          for (let i = 0; i < missing.length; i += 25) {
            if (cancelled) return;
            const chunk = missing.slice(i, i + 25);
            try {
              const q = `?symbols=${encodeURIComponent(chunk.join(','))}&limit=${chunk.length}`;
              const data = await api<{ oi: Record<string, number | null> }>(`/api/market/oi-board${q}`);
              if (cancelled) return;
              setOiMap((prev) => mergeBoard(prev, data.oi));
            } catch { /* keep trying */ }
            await sleep(140);
          }
          await sleep(220);
        }
      } finally {
        if (!cancelled) setOiBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, fillTargets, isSpot]);

  const headBtn = (key: SortKey, align: 'left' | 'right' = 'right'): CSSProperties => ({
    ...colHeadBtn,
    textAlign: align,
    color: sortKey === key ? 'var(--gold)' : 'var(--muted)',
  });

  const filterRow = (
    key: FilterKey,
    label: string,
    on: boolean,
    setOn: (v: boolean) => void,
    min: string,
    setMin: (v: string) => void,
    max: string,
    setMax: (v: string) => void,
    phMin: string,
    phMax: string,
    compactHint?: string,
  ) => (
    <label
      key={key}
      style={{
        ...filterChip,
        borderColor: on ? 'rgba(201,162,39,0.55)' : 'var(--line)',
        background: on ? 'rgba(201,162,39,0.08)' : 'var(--panel-2)',
        opacity: on ? 1 : 0.72,
      }}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => setOn(e.target.checked)}
        style={{ margin: 0, accentColor: '#c9a227' }}
        title={t.pairFilterUse}
      />
      <span style={{ color: on ? 'var(--gold)' : 'var(--muted)', fontWeight: 700, minWidth: 36 }}>{label}</span>
      <input
        value={min}
        onChange={(e) => { setMin(e.target.value); if (!on && e.target.value) setOn(true); }}
        placeholder={phMin}
        disabled={!on && !min && !max}
        style={{ ...rangeInput, opacity: on ? 1 : 0.55 }}
        inputMode="decimal"
        title={compactHint}
      />
      <span style={{ color: 'var(--muted)' }}>–</span>
      <input
        value={max}
        onChange={(e) => { setMax(e.target.value); if (!on && e.target.value) setOn(true); }}
        placeholder={phMax}
        disabled={!on && !min && !max}
        style={{ ...rangeInput, opacity: on ? 1 : 0.55 }}
        inputMode="decimal"
        title={compactHint}
      />
    </label>
  );

  const cellBase: CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', zIndex: 30, flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...triggerBtn,
          minWidth: narrow ? 108 : 132,
          padding: narrow ? '6px 8px' : '7px 10px',
          borderColor: open ? 'var(--gold)' : 'var(--line)',
          color: open ? 'var(--gold)' : 'var(--text)',
        }}
        title={t.pair}
      >
        <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{value || '—'}</span>
        <span style={{ color: 'var(--muted)', fontSize: 11 }}>{open ? '▴' : '▾'}</span>
      </button>

      {open ? (
        <div style={{
          ...panel,
          // Phone: full-bleed board. Desktop: anchored dropdown.
          left: 0,
          right: narrow ? 0 : 'auto',
          width: narrow ? 'auto' : 560,
          maxWidth: narrow ? 'none' : 'min(560px, 96vw)',
          position: narrow ? 'fixed' : 'absolute',
          top: narrow ? 'max(48px, calc(env(safe-area-inset-top, 0px) + 40px))' : '110%',
          bottom: narrow ? 0 : undefined,
          maxHeight: narrow ? undefined : undefined,
          display: narrow ? 'flex' : undefined,
          flexDirection: narrow ? 'column' : undefined,
          borderRadius: narrow ? '12px 12px 0 0' : 12,
          zIndex: narrow ? 90 : undefined,
        }}>
          <div style={{
            ...panelHead,
            gridTemplateColumns: narrow ? '1fr' : 'auto 1fr',
            gap: narrow ? 6 : 8,
          }}>
            <div style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }} role="group" aria-label="Market">
              {([
                ['futures', 'Futures'] as const,
                ['spot', 'Spot'] as const,
              ]).map(([id, label]) => {
                const on = marketType === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onMarketType?.(id)}
                    style={{
                      ...colHeadBtn,
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
                      color: on ? 'var(--gold)' : 'var(--muted)',
                      background: on ? 'rgba(201,162,39,0.12)' : 'transparent',
                      fontWeight: on ? 700 : 500,
                      letterSpacing: 0.04,
                      cursor: onMarketType ? 'pointer' : 'default',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <input
              value={filter}
              onChange={(e) => onFilter(e.target.value.toUpperCase())}
              placeholder={t.filter}
              style={filterInput}
              autoFocus
            />
          </div>

          <div style={{ ...filtersBlock, padding: narrow ? '6px 8px 8px' : '8px 10px 10px' }}>
            <div style={filtersTitle}>{t.pairSuperFilter}</div>
            <div style={{
              ...filtersGrid,
              gridTemplateColumns: narrow ? '1fr 1fr' : '1fr 1fr',
              gap: narrow ? 4 : 6,
            }}>
              {filterRow('pct', '24h %', usePct, setUsePct, pctMin, setPctMin, pctMax, setPctMax, '-5', '15')}
              {filterRow('vol', 'Vol', useVol, setUseVol, volMin, setVolMin, volMax, setVolMax, '1M', '50M', t.pairVolHint)}
              {filterRow('rsi', 'RSI', useRsi, setUseRsi, rsiMin, setRsiMin, rsiMax, setRsiMax, '0', '30')}
              {!isSpot
                ? filterRow('oi', 'OI', useOi, setUseOi, oiMin, setOiMin, oiMax, setOiMax, '100K', '10M', t.pairOiHint)
                : null}
            </div>
          </div>

          <div style={{
            ...colHead,
            gridTemplateColumns: gridCols,
            padding: narrow ? '3px 4px' : '4px 6px',
            gap: narrow ? 2 : 4,
          }}>
            <button type="button" style={headBtn('alpha', 'left')} onClick={() => toggleSort('alpha')} title={t.pairSortCol}>
              {narrow ? 'Pair' : t.pair}{sortMark('alpha')}
            </button>
            <button type="button" style={headBtn('change')} onClick={() => toggleSort('change')} title={t.pairSortCol}>
              {narrow ? '24h' : '24h %'}{sortMark('change')}
            </button>
            <button type="button" style={headBtn('vol')} onClick={() => toggleSort('vol')} title={t.pairSortCol}>
              Vol{sortMark('vol')}
            </button>
            <button type="button" style={headBtn('rsi')} onClick={() => toggleSort('rsi')} title={t.pairSortCol}>
              RSI{sortMark('rsi')}
            </button>
            {!isSpot ? (
              <button type="button" style={headBtn('oi')} onClick={() => toggleSort('oi')} title={t.pairSortCol}>
                OI{sortMark('oi')}
              </button>
            ) : null}
          </div>
          <div style={{ ...listBox, maxHeight: narrow ? undefined : 320, flex: narrow ? 1 : undefined, minHeight: 0 }}>
            {busy && !Object.keys(tickers).length ? <div style={hint}>{t.pairLoading}</div> : null}
            {rsiBusy && !Object.keys(rsiMap).length ? <div style={hint}>{t.pairRsiLoading}</div> : null}
            {!isSpot && oiBusy && !Object.keys(oiMap).length ? <div style={hint}>{t.pairOiLoading}</div> : null}
            {rows.map((sym) => {
              const tk = tickers[sym];
              const pct = tk?.priceChangePercent;
              const rsi = rsiMap[sym];
              const oi = oiMap[sym];
              const active = sym === value;
              const tickPending = busy && !tk;
              const rsiPending = (rsi == null || !Number.isFinite(rsi)) && (rsiBusy || tickPending);
              const oiPending = !isSpot && (oi == null || !Number.isFinite(oi)) && (oiBusy || tickPending);
              return (
                <button
                  key={sym}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPick(sym);
                    setOpen(false);
                  }}
                  style={{
                    ...rowBtn,
                    gridTemplateColumns: gridCols,
                    gap: narrow ? 2 : 4,
                    padding: narrow ? '6px 4px' : '7px 6px',
                    fontSize: narrow ? 11 : 12,
                    borderColor: active ? 'var(--gold)' : 'transparent',
                    background: active ? 'rgba(201,162,39,0.1)' : 'transparent',
                  }}
                >
                  <span style={{ ...cellBase, fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--text)' }}>{sym}</span>
                  <span style={{
                    ...cellBase,
                    textAlign: 'right', fontFamily: 'var(--mono)',
                    color: pct == null ? 'var(--muted)' : pct >= 0 ? 'var(--long)' : 'var(--short)',
                  }}>
                    {pct == null || !Number.isFinite(pct) ? (tickPending ? '…' : '—') : fmtPct(pct)}
                  </span>
                  <span style={{ ...cellBase, textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
                    {tk
                      ? fmtCompact(
                        tk.quoteVolume > 0
                          ? tk.quoteVolume
                          : ((Number(tk.volume) || 0) * (Number(tk.lastPrice) || 0)),
                      )
                      : (tickPending ? '…' : '—')}
                  </span>
                  <span style={{
                    ...cellBase,
                    textAlign: 'right', fontFamily: 'var(--mono)',
                    color: rsi == null ? 'var(--muted)'
                      : rsi >= 70 ? 'var(--short)'
                        : rsi <= 30 ? 'var(--long)' : 'var(--text)',
                  }}>
                    {rsi == null || !Number.isFinite(rsi) ? (rsiPending ? '…' : '—') : rsi.toFixed(0)}
                  </span>
                  {!isSpot ? (
                    <span style={{ ...cellBase, textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
                      {oi == null || !Number.isFinite(oi) ? (oiPending ? '…' : '—') : fmtCompact(oi)}
                    </span>
                  ) : null}
                </button>
              );
            })}
            {!rows.length ? <div style={hint}>{t.pairEmpty}</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const triggerBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  minWidth: 132, padding: '7px 10px', borderRadius: 8,
  border: '1px solid var(--line)', background: 'var(--panel-2)', cursor: 'pointer',
};
const panel: CSSProperties = {
  position: 'absolute', left: 0, top: '110%', zIndex: 80,
  width: 560, maxWidth: 'min(560px, 96vw)',
  borderRadius: 12, border: '1px solid var(--line)',
  // Theme tokens — hardcoded dark made Ivory pair names invisible (dark on dark).
  background: 'var(--panel)',
  color: 'var(--text)',
  boxShadow: '0 14px 36px var(--overlay-scrim)',
  overflow: 'hidden',
};
const panelHead: CSSProperties = {
  display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, alignItems: 'center',
  padding: '10px 10px 8px', borderBottom: '1px solid var(--line)',
};
const filterInput: CSSProperties = {
  width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid var(--line)',
  background: 'var(--panel-2)', color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)',
};
const filtersBlock: CSSProperties = {
  padding: '8px 10px 10px', borderBottom: '1px solid var(--line)',
};
const filtersTitle: CSSProperties = {
  fontSize: 10, color: 'var(--muted)', letterSpacing: 0.06, textTransform: 'uppercase',
  marginBottom: 6, fontWeight: 600,
};
const filtersGrid: CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
};
const filterChip: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '4px 6px', borderRadius: 8, border: '1px solid var(--line)',
  fontSize: 11, cursor: 'pointer', userSelect: 'none', minWidth: 0,
};
const rangeInput: CSSProperties = {
  width: 48, minWidth: 0, flex: '1 1 40px', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--line)',
  background: 'var(--panel)', color: 'var(--text)', fontSize: 11, fontFamily: 'var(--mono)',
};
const colHead: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1.35fr) minmax(0,0.7fr) minmax(0,0.55fr) minmax(0,0.45fr) minmax(0,0.6fr)',
  gap: 4,
  padding: '4px 6px', borderBottom: '1px solid var(--line)',
  alignItems: 'center',
};
const colHeadBtn: CSSProperties = {
  padding: '4px 2px', border: 'none', background: 'transparent', cursor: 'pointer',
  fontSize: 10, fontWeight: 700, letterSpacing: 0.02, color: 'var(--muted)',
  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const listBox: CSSProperties = { maxHeight: 320, overflow: 'auto', padding: 6 };
const rowBtn: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1.35fr) minmax(0,0.7fr) minmax(0,0.55fr) minmax(0,0.45fr) minmax(0,0.6fr)',
  gap: 4, width: '100%',
  padding: '7px 6px', borderRadius: 8, border: '1px solid transparent',
  background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 12, textAlign: 'left',
  alignItems: 'center',
};
const hint: CSSProperties = { padding: 10, fontSize: 12, color: 'var(--muted)', textAlign: 'center' };
